import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, X } from "lucide-react";
import api from "../lib/api";
import { useVisibleBlogs } from "../lib/visibleBlogs";
import { useLang, BLOG_INFO } from "../context/LangContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BlogBadge from "../components/BlogBadge";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import PromoBlock from "../components/PromoBlock";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const HERO = {
  suica: "https://images.unsplash.com/photo-1747574172799-5ef1d0fcd8f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  desenvolvimento: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  automacao: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};
const INTRO = {
  suica: { pt: "Guias e experiências reais sobre viver, trabalhar e tratar da burocracia na Suíça.", en: "Real guides and experiences on living, working and handling bureaucracy in Switzerland." },
  desenvolvimento: { pt: "Tutoriais e boas práticas de engenharia: Java, Spring Boot, Supabase e React Native.", en: "Tutorials and engineering best practices: Java, Spring Boot, Supabase and React Native." },
  automacao: { pt: "Automações e produtividade: n8n, CI/CD, cron jobs e fluxos que poupam horas.", en: "Automation and productivity: n8n, CI/CD, cron jobs and time-saving workflows." },
};
const PAGE_SIZE = 9;

export default function BlogPage() {
  const { blog } = useParams();
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const visibleBlogs = useVisibleBlogs();
  const info = BLOG_INFO[blog];
  const blogName = info ? (lang === "pt" ? info.pt : info.en) : blog;
  useDocumentTitle(`${blogName} · ReiMultiBlog`, lang === "pt" ? INTRO[blog]?.pt : INTRO[blog]?.en);

  useEffect(() => { setPage(1); setQ(""); setQuery(""); setCat(""); }, [blog]);
  useEffect(() => { api.get("/public/categories").then((r) => setCats(r.data[blog] || [])).catch(() => {}); }, [blog]);
  useEffect(() => { const id = setTimeout(() => { setQuery(q); setPage(1); }, 400); return () => clearTimeout(id); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ blog, page: String(page), page_size: String(PAGE_SIZE) });
      if (query.trim()) params.set("q", query.trim());
      if (cat) params.set("category", cat);
      const { data } = await api.get(`/public/articles?${params.toString()}`);
      setItems(data.items || []); setPages(data.pages || 1); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [blog, page, query, cat]);
  useEffect(() => { load(); }, [load]);

  if (!visibleBlogs) {
    return <div className="App relative"><Navbar /><main data-testid="blog-loading" className="min-h-[60vh]" /></div>;
  }
  if (!visibleBlogs.includes(blog)) {
    return (
      <div className="App relative">
        <Navbar />
        <main data-testid="blog-unavailable" className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="font-heading text-4xl font-bold">{t("Blog indisponível", "Blog unavailable")}</h1>
          <Link to="/" className="mt-6 inline-block text-primary font-semibold">{t("Voltar ao início", "Back to home")}</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App relative">
      <Navbar />
      <JsonLd data={{ "@context": "https://schema.org", "@type": "Blog", name: `${blogName} · ReiMultiBlog`, description: lang === "pt" ? INTRO[blog]?.pt : INTRO[blog]?.en }} />
      <section className="relative h-64 overflow-hidden border-b border-white/10">
        <img src={HERO[blog]} alt={blogName} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
        <div className="absolute inset-0" style={{ background: `radial-gradient(700px circle at 20% 80%, ${info?.color}33, transparent 60%)` }} />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <BlogBadge blog={blog} />
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{blogName}</h1>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs items={[{ label: blogName }]} />
        <p className="mt-4 max-w-2xl text-muted-foreground">{lang === "pt" ? INTRO[blog]?.pt : INTRO[blog]?.en}</p>

        {/* Categories */}
        {cats.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="category-filter">
            <button onClick={() => { setCat(""); setPage(1); }} data-testid="category-all" className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${cat === "" ? "border-primary bg-primary/10 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>{t("Todos", "All")}</button>
            {cats.map((c) => (
              <button key={c.id} data-testid={`category-${c.id}`} onClick={() => { setCat(c.id); setPage(1); }} className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${cat === c.id ? "border-primary bg-primary/10 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>{lang === "pt" ? c.pt : c.en}</button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="mt-6 mb-8 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="blog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Procurar artigos…", "Search articles…")} className="pl-9 pr-9" />
            {q && <button data-testid="blog-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
          </div>
          {!loading && <span className="text-sm text-muted-foreground" data-testid="blog-result-count">{total} {t("resultado(s)", "result(s)")}</span>}
        </div>

        {loading ? (
          <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-card p-12 text-center">
            <p className="text-muted-foreground">{query || cat ? t("Nenhum artigo corresponde ao filtro.", "No articles match your filter.") : t("Ainda não há artigos publicados neste blog.", "No published articles in this blog yet.")}</p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {items.map((a) => (
                <Link key={a.id} to={`/blog/${a.blog}/${a.slug}`} data-testid={`article-card-${a.id}`} className="group rounded-2xl border border-white/10 bg-card overflow-hidden">
                  {a.images?.hero && <img src={a.images.hero} alt={a.title} className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold leading-snug group-hover:text-primary transition-colors">{a.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
                    <p className="mt-3 text-xs text-muted-foreground">{new Date(a.published_at).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB")}</p>
                  </div>
                </Link>
              ))}
            </div>
            {pages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-3" data-testid="blog-pagination">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev" className="gap-1.5"><ChevronLeft className="h-4 w-4" />{t("Anterior", "Prev")}</Button>
                <span className="text-sm text-muted-foreground" data-testid="page-indicator">{t("Página", "Page")} {page} / {pages}</span>
                <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="page-next" className="gap-1.5">{t("Seguinte", "Next")}<ChevronRight className="h-4 w-4" /></Button>
              </div>
            )}
          </>
        )}

        <div className="mt-12 max-w-md"><PromoBlock /></div>
      </section>
      <Footer />
    </div>
  );
}
